import * as Y from "yjs";
import { LOCAL_ORIGIN } from "./origins";

// These map names MUST match schema.ts (ENTITIES_MAP / STORY_MAP and the
// deletedEntities tombstone map). The UndoManager tracks all three:
//   - entities          → create / delete / reorder / settings edits
//   - story             → story settings + design system
//   - deletedEntities   → so undoing a delete also reverts its tombstone,
//                         re-enabling edits to the restored entity
const TRACKED_MAPS = ["entities", "story", "deletedEntities"] as const;

// Yjs's default. Rapid same-origin changes — a typed word, a drag — coalesce
// into one undo step; a change landing later than this starts a new one.
export const DEFAULT_CAPTURE_TIMEOUT_MS = 500;

// Op types that should each be their own undo step. Pure settings edits group
// by time (captureTimeout); these get an explicit boundary via stopCapturing()
// at the call site so e.g. "add element" never merges into a prior text edit.
export const STRUCTURAL_OP_TYPES: ReadonlySet<string> = new Set([
  "create-entity",
  "delete-entity",
  "reorder-entity",
  "restore-story",
  "update-design-system",
  "change-layout",
]);

// The stack-item meta key an AI turn's step is tagged with, so the AI Undo
// affordances can tell "the newest undo step is this turn" from "the user has
// changed something since" — the same rule the Redux history's frame
// `aiActionId` carries in single-seat editing.
export const AI_ACTION_ID_META_KEY = "aiActionId";

// Builds the collaborative undo/redo manager for a story Y.Doc.
//
// Why this replaces the hand-rolled mapHistoryRestore: the UndoManager computes
// inverses on the CRDT structure itself (it knows exactly which items the local
// user inserted/deleted/changed and reverses only those, respecting peers'
// concurrent edits). The inverse is applied back to the Y.Doc and flows to
// Redux + peers through the same observer path as any remote change — no need
// to re-derive reducer side-effects by hand.
export const createStoryUndoManager = (doc: Y.Doc): Y.UndoManager => {
  const scopes = TRACKED_MAPS.map((name) => doc.getMap(name));
  return new Y.UndoManager(scopes, {
    // Only locally-authored changes (the bridge tags them LOCAL_ORIGIN) enter
    // the stack. Remote peers' edits carry a different origin and are never
    // captured — this is what makes undo *local*: you only ever undo your own
    // work, never a teammate's. (Yjs's blessed pattern for collaborative undo.)
    trackedOrigins: new Set([LOCAL_ORIGIN]),
    // Rapid same-origin changes coalesce into a single undo step. Discrete
    // structural actions get an explicit boundary via stopCapturing() at the
    // call site — see STRUCTURAL_OP_TYPES and createAiBatchCapture.
    captureTimeout: DEFAULT_CAPTURE_TIMEOUT_MS,
  });
};

type StackItemLike = { meta: Map<string, unknown> };

export type AiBatchCapture = {
  // The Redux `history.activeAiActionId` went null → id: everything the turn
  // writes from here on is ONE stack item.
  begin: (aiActionId: string) => void;
  // The turn is over (id → null): normal capture rules resume, and the next
  // edit starts its own step.
  end: () => void;
  // begin/end driven by the store's current value; repeats are ignored.
  sync: (aiActionId: string | null) => void;
  activeId: () => string | null;
  // For the provider's stack-item-added handler: stamps the active turn's id
  // on the item. Merged writes fire stack-item-updated instead, so the tag is
  // written once and stays on the one item.
  tag: (stackItem: StackItemLike) => void;
  // The id on the newest undo step, or null when the newest step is not an AI
  // turn's (a user edit, or an empty stack).
  topAiActionId: () => string | null;
  // Whether applyEvent should put a stopCapturing() boundary around these
  // ops: yes for a structural op on its own, never inside an AI batch —
  // a turn that inserts a slide, or restyles then reorders, is still one step.
  shouldStopCapturingFor: (opTypes: readonly string[]) => boolean;
};

// Makes an active AI turn one Yjs undo step. The UndoManager only knows time
// (captureTimeout) and explicit boundaries (stopCapturing); a turn's writes
// arrive over seconds, interleaved with structural ops, so without this a
// bulk edit of seven texts undid one text at a time in a room, while the Redux
// reducer's aiActionId collapse (store/story/index.ts handleUndo) never ran.
//
// While a batch is active the manager's captureTimeout is effectively
// infinite — Yjs reads the instance property at every afterTransaction — and
// the provider skips its structural boundaries, so every write appends to the
// item begin() opened. The item is tagged with the turn's id (see
// AI_ACTION_ID_META_KEY) so the affordances can gate on the real stack top.
// A user edit landing mid-turn merges into the AI item too — parity with Redux
// mode, where handleHistory tags every frame while activeAiActionId is set.
export const createAiBatchCapture = (
  undoManager: Y.UndoManager,
): AiBatchCapture => {
  let active: string | null = null;

  const begin = (aiActionId: string) => {
    active = aiActionId;
    // The turn must not merge into the user's own edit just before it.
    undoManager.stopCapturing();
    undoManager.captureTimeout = Number.MAX_SAFE_INTEGER;
  };

  const end = () => {
    active = null;
    undoManager.captureTimeout = DEFAULT_CAPTURE_TIMEOUT_MS;
    // And the user's next edit must not merge into the turn.
    undoManager.stopCapturing();
  };

  const sync = (aiActionId: string | null) => {
    if (aiActionId === active) return;
    if (active !== null) end();
    if (aiActionId !== null) begin(aiActionId);
  };

  const tag = (stackItem: StackItemLike) => {
    if (active === null) return;
    stackItem.meta.set(AI_ACTION_ID_META_KEY, active);
  };

  const topAiActionId = (): string | null => {
    const { undoStack } = undoManager;
    const top = undoStack[undoStack.length - 1];
    const id = top?.meta.get(AI_ACTION_ID_META_KEY);
    return typeof id === "string" ? id : null;
  };

  const shouldStopCapturingFor = (opTypes: readonly string[]) =>
    active === null && opTypes.some((type) => STRUCTURAL_OP_TYPES.has(type));

  return {
    begin,
    end,
    sync,
    activeId: () => active,
    tag,
    topAiActionId,
    shouldStopCapturingFor,
  };
};
